import { useMemo, useState } from 'react';
import cm from 'classnames';

import { Folder, FolderType } from 'services/folders.service';
import { useAppSelector, useAppDispatch } from 'store/store-hooks';
import { createFolder, fetchFolders, updateEntity, setRemovingInstance } from 'components/app/_mediator/app-data.slice';
import { ModalEnum, setModal } from 'components/app/_mediator/app.slice';
import { sortProjectEntities } from '../_mediator/projects.helper';
import { setSelectedFolder, setTableState } from '../_mediator/projects.slice';
import { getFoldersSortCb, PROJECTS_FOLDERS_HEADERS } from '../_mediator/projects.params';
import { useProjectsSearch } from '../use-projects-search';

import { ReactComponent as TrashIcon } from 'assets/ui/trash.svg';
import { ReactComponent as PlusIcon } from 'assets/ui/plus.svg';

import { EditCell } from 'components/app/app-components/edit-cell/edit-cell';
import { CreateElementModal } from '../elements/modal-create-element';
import { HeaderFolders } from '../elements/header-folders';
import { HeaderAction } from '../elements/header-action';
import { HeaderSearch } from '../elements/header-search';
import { ProjectsTable } from './elements/projects-table';
import { SectionHeader } from './elements/section-header';
import { Section } from './elements/section';
import styles from './_sections.module.scss';

export const SectionFolders = () => {
  const { selectedFolder, foldersTableState, foldersTableSort } = useAppSelector((state) => state.projects);
  const { folders, removingInstance } = useAppSelector((state) => state.app__data);
  const modal = useAppSelector((state) => state.app.modal);
  const dispatch = useAppDispatch();

  const { searchValue, updateSearchValue, getFilteredList, clearSearchValue } =
    useProjectsSearch('foldersSearchFilter');

  const [folderName, setFolderName] = useState('');

  const isDisabled = useMemo(() => {
    return !selectedFolder || removingInstance?.instance !== 'folder';
  }, [selectedFolder, removingInstance?.instance]);

  const customFolders = useMemo(() => {
    if (!folders) return [];
    return folders.filter((el) => el.type === FolderType.Custom);
  }, [folders]);

  const filteredFolders = useMemo(() => {
    return getFilteredList<Folder>(customFolders);
  }, [customFolders, searchValue]);

  const sortedFolders = useMemo(() => {
    return sortProjectEntities(
      filteredFolders,
      foldersTableSort.order,
      getFoldersSortCb(PROJECTS_FOLDERS_HEADERS[foldersTableSort.column])
    );
  }, [filteredFolders, foldersTableSort]);

  const handleSelect = (folderId: number) => {
    dispatch(setRemovingInstance({ id: folderId, instance: 'folder', modalOpen: false }));
    dispatch(setSelectedFolder(folderId));
  };

  const handleCreate = async () => {
    await dispatch(createFolder(folderName)).unwrap();
    await dispatch(fetchFolders())
      .unwrap()
      .then((folders) => {
        const createdFolder = folders.find((el) => el.name === folderName);
        if (createdFolder) {
          dispatch(setSelectedFolder(createdFolder.id));
          dispatch(setTableState({ key: 'foldersTableState', value: true }));
        } else console.error('folder not found');
      });
    clearSearchValue(folderName);
    setFolderName('');
    dispatch(setRemovingInstance(null));
  };

  return (
    <Section expanded={!!customFolders.length && foldersTableState}>
      <SectionHeader
        expanded={foldersTableState}
        keyTableState="foldersTableState"
        title="Folders"
        empty={!folders?.length}
        len={sortedFolders.length}
      >
        <HeaderFolders />
        <HeaderSearch
          searchValue={searchValue}
          updateSearchValue={updateSearchValue}
          clearSearchValue={clearSearchValue}
        />
        <HeaderAction
          type="create"
          icon={<PlusIcon />}
          onSubmit={() => dispatch(setModal(ModalEnum.addFolder))}
          disabled={false}
          tip="Add a folder"
        />
        <HeaderAction
          type="delete"
          icon={<TrashIcon />}
          onSubmit={() => {
            removingInstance?.instance === 'folder' &&
              dispatch(setRemovingInstance({ ...removingInstance, modalOpen: true }));
          }}
          disabled={isDisabled}
          tip={isDisabled ? 'Select a folder for deletion' : 'Delete folder'}
        />
      </SectionHeader>

      <div className={styles.SectionBody}>
        {customFolders && customFolders.length !== 0 && (
          <ProjectsTable
            sortKey="foldersTableSort"
            headers={PROJECTS_FOLDERS_HEADERS}
            selectedRow={selectedFolder ?? undefined}
          >
            {sortedFolders?.map((row) => (
              <tr
                key={row.id}
                onClick={() => handleSelect(row.id)}
                className={cm({
                  [styles.selected]: row.id === selectedFolder,
                  [styles.removing]:
                    removingInstance?.instance === 'folder' &&
                    removingInstance?.modalOpen &&
                    removingInstance?.id === row.id,
                })}
              >
                <EditCell
                  element={{ name: row.name, id: row.id }}
                  onSubmit={(element) =>
                    dispatch(updateEntity({ id: element.id, name: element.name, entity: 'folders' }))
                  }
                  selectedID={selectedFolder}
                  selectedName={folders?.find((el) => el.id === selectedFolder)?.name ?? ''}
                  width={13}
                />
                <td></td>
                <td></td>
              </tr>
            ))}
          </ProjectsTable>
        )}
      </div>

      {/* modals */}

      <CreateElementModal
        isOpen={modal === ModalEnum.addFolder}
        onClose={() => {
          dispatch(setModal(0));
          setFolderName('');
        }}
        onSubmit={() => {
          dispatch(setModal(0));
          handleCreate();
        }}
        modalTitle="Name your folder"
        inputValue={folderName}
        onInputChange={(value) => setFolderName(value)}
        disabled={!folderName}
      />
    </Section>
  );
};
